import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAttendenceData } from '../../services/dataService';
import { getSecureItem } from '../../services/SecureStorage';

function NoPunch() {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedProfitCenterName, setSelectedProfitCenterName] = useState('');
    const isAdmin = getSecureItem("IsAdmin");

    const location = useLocation();
    const navigate = useNavigate();
    const UID = getSecureItem("userId");

    // Get profit center name from location state
    const { selectedProfitCenter } = location.state || {};
    const profitCenterName = selectedProfitCenter || '';

    // Set default profit center filter when component mounts
    useEffect(() => {
        if (profitCenterName) {
            setSelectedProfitCenterName(profitCenterName);
        }
    }, [profitCenterName]);

    const getAttendence = async (date = selectedDate) => {
        try {
            setLoading(true);
            const payload = {
                Date: date,
                UserId: UID
            };

            const response = await getAttendenceData(payload);

            // Filter absent employees
            const absentEmployees = response.data.data.filter(employee => isAbsent(employee));
            setAttendanceData(absentEmployees);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAttendence();
    }, [selectedDate]);

    const isDefaultTime = (time) => {
        return time === "1900-01-01T00:00:00.000Z" || time === null || time === undefined;
    };

    const isAbsent = (row) => {
        return isDefaultTime(row.PUNCHIN) && isDefaultTime(row.PUNCHOUT);
    };

    // Get unique departments and profit centers
    const departments = useMemo(() => {
        const uniqueDepts = [...new Set(attendanceData.map(emp => emp.DepartmentName))];
        return uniqueDepts.filter(dept => dept);
    }, [attendanceData]);

    const profitCenters = useMemo(() => {
        const uniqueCenters = [...new Set(attendanceData.map(emp => emp.ProfitcenterName))];
        return uniqueCenters.filter(center => center);
    }, [attendanceData]);

    // Filter data based on search and selections
    const filteredData = useMemo(() => {
        return attendanceData.filter(employee => {
            const matchesSearch = employee.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                employee.EmpCode?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesDepartment = !selectedDepartment || employee.DepartmentName === selectedDepartment;
            const matchesProfitCenter = !selectedProfitCenterName || employee.ProfitcenterName === selectedProfitCenterName;

            return matchesSearch && matchesDepartment && matchesProfitCenter;
        });
    }, [attendanceData, searchTerm, selectedDepartment, selectedProfitCenterName]);

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        const time = new Date(timeString);
        return time.toISOString().substr(11, 5); // Extract "HH:mm" from the ISO string
    };


    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            {/* Header with Back Button */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '30px'
            }}>
                <h2 style={{ margin: '0', color: '#333', fontSize: '28px' }}>
                    Not Punched Employees  {selectedProfitCenter ? `on ${selectedProfitCenter}` : ''}

                </h2>
                {isAdmin == 0 && filteredData?.length > 0 && (
                    <>
                        <p className="text-2xl text-gray-800">
                            Department: {[...new Set(filteredData.map((el) => el.DepartmentName))].join(", ")}
                        </p>
                    </>
                )}
                <button
                    onClick={() => navigate('/')}
                    style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'background-color 0.3s ease'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                    className="btn btn-danger"
                >
                    ← Back
                </button>
            </div>

            {/* Filters */}
            <div style={{
                display: 'flex',
                gap: '15px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                {/* Date Input */}
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Date:
                    </label>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        style={{
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    />
                </div>

                {/* Search Input */}
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Search:
                    </label>
                    <input
                        type="text"
                        placeholder="Search by name or code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            width: '200px'
                        }}
                    />
                </div>

                {/* Department Filter */}
                <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Department:
                    </label>
                    <select
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        style={{
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                            width: '180px'
                        }}
                    >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                        ))}
                    </select>
                </div>

                {/* Profit Center Filter */}
                {!selectedProfitCenter &&
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Profit Center:
                        </label>
                        <select
                            value={selectedProfitCenterName}
                            onChange={(e) => setSelectedProfitCenterName(e.target.value)}
                            style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                width: '180px'
                            }}
                        >
                            <option value="">All Profit Centers</option>
                            {profitCenters.map(center => (
                                <option key={center} value={center}>{center}</option>
                            ))}
                        </select>
                    </div>}
            </div>

            {/* Count Display */}
            <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e9ecef'
            }}>
                <h3 style={{ margin: '0', color: '#495057' }}>
                    Total UnPunched: <span style={{ color: '#dc3545' }}>{filteredData.length}</span>
                </h3>
            </div>

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <p>Loading attendance data...</p>
                </div>
            )}

            {/* Table */}
            {!loading && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <th style={tableHeaderStyle}>Sl No</th>
                                <th style={tableHeaderStyle}>Employee Name</th>
                                <th style={tableHeaderStyle}>Code</th>
                                <th style={tableHeaderStyle}>Department</th>

                                <th style={tableHeaderStyle}>Shift Name</th>


                                <th style={tableHeaderStyle}>Start Time</th>
                                <th style={tableHeaderStyle}>End Time</th>
                                <th style={tableHeaderStyle}>Punch In</th>
                                <th style={tableHeaderStyle}>Punch Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{
                                        textAlign: 'center',
                                        padding: '40px',
                                        color: '#6c757d',
                                        fontStyle: 'italic'
                                    }}>
                                        No absent employees found for the selected criteria
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((employee, index) => (
                                    <tr key={employee.ID} style={{
                                        borderBottom: '1px solid #dee2e6',
                                        '&:hover': { backgroundColor: '#f8f9fa' }
                                    }}>
                                        <td style={tableCellStyle}>{index + 1}</td>
                                        <td style={tableCellStyle}>{employee.EmployeeName || 'N/A'}</td>
                                        <td style={tableCellStyle}>{employee.EmpCode || 'N/A'}</td>
                                        <td style={tableCellStyle}>{employee.DepartmentName || 'N/A'}</td>

                                        <td style={tableCellStyle}>{employee.ShiftName || 'N/A'}</td>

                                        <td style={tableCellStyle}>{formatTime(employee.StartTime)}</td>
                                        <td style={tableCellStyle}>{formatTime(employee.EndTime)}</td>
                                        <td style={tableCellStyle}>
                                            <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                                {formatTime(employee.PUNCHIN)}
                                            </span>
                                        </td>
                                        <td style={tableCellStyle}>
                                            <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                                {formatTime(employee.PUNCHOUT)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left',
    fontWeight: 'bold',
    borderBottom: '2px solid #dee2e6',
    backgroundColor: '#e9ecef',
    color: '#495057'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    color: '#212529'
};

export default NoPunch;